import { START_MONTH } from '../constants';
import { parseCohortDate } from './parseCohortDate';

/**
 * planStartMonth in model state uses 0-based month index (0=Jan, same as Date.getMonth()).
 * Older AI applies stored 1-based calendar month (1=Jan); migrate when it matches today.
 */
export function resolvePlanStartMonth(state) {
  const raw = state?.planStartMonth ?? START_MONTH;
  if (state?.firstPlanMonthIsCurrentMonth === true) {
    const currentCalMonth = new Date().getMonth() + 1;
    if (raw === currentCalMonth) {
      return currentCalMonth - 1;
    }
  }
  return raw;
}

function addCalendarMonths(year, month, offset) {
  const zeroBased = month - 1 + offset;
  return {
    year: year + Math.floor(zeroBased / 12),
    month: (zeroBased % 12) + 1,
  };
}

/** Latest cohort calendar month (1-based month) from cohortSheet, or null. */
export function getLatestCohortCalendarMonth(cohortSheet) {
  if (!cohortSheet?.cohorts?.length) {
    return null;
  }
  let latest = null;
  for (const cohort of cohortSheet.cohorts) {
    const parsed = parseCohortDate(cohort.cohortDate);
    if (!parsed) {
      continue;
    }
    if (
      !latest ||
      parsed.year > latest.year ||
      (parsed.year === latest.year && parsed.month > latest.month)
    ) {
      latest = parsed;
    }
  }
  return latest;
}

/**
 * First plan month = calendar month after latest cohortDate.
 * Returns { year, month } with 1-based month, or null when no cohorts.
 */
export function getPlanStartAfterCohortSheet(cohortSheet) {
  const latest = getLatestCohortCalendarMonth(cohortSheet);
  if (!latest) {
    return null;
  }
  return addCalendarMonths(latest.year, latest.month, 1);
}

export function hasCohortPlanAnchor(state) {
  return (state?.cohortSheet?.cohorts?.length ?? 0) > 0;
}

/** Marketing table: row i = plan start + i when AI/current-month or cohort-anchored. */
export function isCalendarPlanLayout(state) {
  return (
    state?.firstPlanMonthIsCurrentMonth === true || hasCohortPlanAnchor(state)
  );
}

/**
 * Results table: column i = plan start + i.
 * Without cohort data, align to plan start (not legacy column 0 = month before plan).
 */
export function isResultsCalendarAligned(state) {
  return (
    hasCohortPlanAnchor(state) ||
    state?.firstPlanMonthIsCurrentMonth === true ||
    !hasCohortPlanAnchor(state)
  );
}

/** Apply cohort-anchored plan start fields onto a model state object. */
export function applyCohortAnchoredPlanStart(state) {
  const anchor = getPlanStartAfterCohortSheet(state?.cohortSheet);
  if (!anchor) {
    return state;
  }
  return {
    ...state,
    planStartYear: anchor.year,
    planStartMonth: anchor.month - 1,
    firstPlanMonthIsCurrentMonth: false,
  };
}
